import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { CheckCircle2, AlertTriangle, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useVatRate, useActiveWithholdingRates } from '@/hooks/finance/useTaxSettings';

interface PaymentConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  procurement: any;
  onConfirm: () => void;
}

type VatMode = 'none' | 'add' | 'deduct';

export const PaymentConfirmationDialog: React.FC<PaymentConfirmationDialogProps> = ({
  open,
  onOpenChange,
  procurement,
  onConfirm
}) => {
  const [actualPaymentDate, setActualPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [paidFromAccountId, setPaidFromAccountId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemsExpanded, setItemsExpanded] = useState(false);
  
  // Tax handling state
  const [vatMode, setVatMode] = useState<VatMode>('none');
  const [withholdingApplicable, setWithholdingApplicable] = useState(false);
  const [selectedWithholdingRateId, setSelectedWithholdingRateId] = useState<string>('');
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);
  const { data: defaultVatRate } = useVatRate();
  const { data: withholdingRates } = useActiveWithholdingRates();

  const items = procurement?.items || [];

  const quality = procurement?.quality;
  const isPreliminary = quality?.grading_type === 'preliminary';
  const isFinalComplete = quality?.is_final_grading_complete;
  
  // Calculate differences between preliminary and final (if both exist)
  const hasFinalGrading = isPreliminary && isFinalComplete;
  const preliminaryCompensation = quality?.preliminary_compensation_kg || 0;
  const finalCompensation = quality?.compensation_kg || 0;
  const compensationDifference = hasFinalGrading ? finalCompensation - preliminaryCompensation : 0;

  // Get the approved payable amount as input
  const approvedAmount = procurement?.finance?.approved_net_payable_amount || procurement.total_amount || 0;

  // Tax calculations
  const paymentTotals = useMemo(() => {
    const vatRate = defaultVatRate ?? 15;
    const selectedWht = withholdingRates?.find(r => r.id === selectedWithholdingRateId);
    const withholdingRate = withholdingApplicable && selectedWht ? selectedWht.rate : 0;
    
    let baseAmount: number;
    let vatAmount: number;
    let subtotal: number;
    
    switch (vatMode) {
      case 'add':
        baseAmount = approvedAmount;
        vatAmount = baseAmount * (vatRate / 100);
        subtotal = baseAmount + vatAmount;
        break;
      case 'deduct':
        subtotal = approvedAmount;
        baseAmount = subtotal / (1 + vatRate / 100);
        vatAmount = subtotal - baseAmount;
        break;
      case 'none':
      default:
        baseAmount = approvedAmount;
        vatAmount = 0;
        subtotal = approvedAmount;
        break;
    }
    
    const withholdingAmount = baseAmount * (withholdingRate / 100);
    const netPayment = subtotal - withholdingAmount;
    
    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      vatRate: vatMode === 'none' ? 0 : vatRate,
      vatAmount: Math.round(vatAmount * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      withholdingRate,
      withholdingAmount: Math.round(withholdingAmount * 100) / 100,
      netPayment: Math.round(netPayment * 100) / 100,
    };
  }, [approvedAmount, vatMode, defaultVatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

  const handleConfirmPayment = async () => {
    if (!user?.id) return;

    setIsSubmitting(true);
    try {
      // Step 1: Generate expense number FIRST
      const { data: expenseNumber, error: numberError } = await supabase
        .rpc('generate_expense_number');
      
      if (numberError || !expenseNumber) {
        console.error('Error generating expense number:', numberError);
        throw new Error('Failed to generate expense number');
      }

      console.log('Generated expense number:', expenseNumber);

      // Step 2: Create expense record with full tax breakdown
      const { data: expense, error: expenseError } = await supabase
        .from('expenses')
        .insert({
          expense_number: expenseNumber,
          expense_date: actualPaymentDate,
          description: `Procurement: ${procurement.product_name || 'Product'} from ${procurement.source_name || 'Supplier'}`,
          vendor_name: procurement.source_name || 'Unknown Supplier',
          reference_number: `PROC-${procurement.id.slice(0, 8)}`,
          amount: paymentTotals.baseAmount,
          currency: procurement.currency || 'ETB',
          vat_rate: paymentTotals.vatRate,
          vat_amount: paymentTotals.vatAmount,
          total_amount: paymentTotals.subtotal,
          withholding_rate: paymentTotals.withholdingRate,
          withholding_amount: paymentTotals.withholdingAmount,
          net_payment: paymentTotals.netPayment,
          payment_status: 'paid',
          payment_date: actualPaymentDate,
          created_by: user.id,
          approved_by: user.id,
          approved_at: new Date().toISOString(),
          bank_account_id: paidFromAccountId || null
        })
        .select('id')
        .single();

      if (expenseError) {
        console.error('Error creating expense:', expenseError);
        throw new Error('Failed to create expense record. Payment not confirmed.');
      }

      console.log('Created expense:', expense);

      // Get selected bank account name for denormalization
      const selectedAccount = bankAccounts?.find(a => a.id === paidFromAccountId);

      // Step 3: Update procurement_finance with payment confirmation AND expense link
      const { error } = await supabase
        .from('procurement_finance')
        .update({
          payment_confirmed: true,
          payment_confirmed_by: user.id,
          payment_confirmed_at: new Date().toISOString(),
          actual_payment_date: actualPaymentDate,
          payment_confirmation_notes: confirmationNotes || null,
          expense_id: expense.id,
          paid_from_account_id: paidFromAccountId || null,
          paid_from_account_name: selectedAccount?.name || null
        })
        .eq('procurement_id', procurement.id);

      if (error) throw error;

      // Step 4: Update procurement status to completed
      const { error: statusError } = await supabase
        .from('procurements')
        .update({ status: 'completed' })
        .eq('id', procurement.id);

      if (statusError) {
        console.error('Error updating procurement status:', statusError);
      }

      toast({
        title: "Payment Confirmed",
        description: "Payment confirmed and expense record created.",
      });

      onConfirm();
      onOpenChange(false);
      
      // Reset form
      setActualPaymentDate(new Date().toISOString().split('T')[0]);
      setConfirmationNotes('');
      setPaidFromAccountId('');
      setVatMode('none');
      setWithholdingApplicable(false);
      setSelectedWithholdingRateId('');
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to confirm payment.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Confirm Payment
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-md">
            <h4 className="font-medium mb-2">Payment Details</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span><span className="font-medium">Product:</span> {procurement.product_name || 'N/A'}</span>
                {items.length > 1 && (
                  <Badge variant="outline" className="text-xs">
                    <Package className="h-3 w-3 mr-1" />
                    {items.length} items
                  </Badge>
                )}
              </div>
              <div><span className="font-medium">Approved Amount:</span> Br{approvedAmount?.toFixed(2) || '0.00'}</div>
              <div><span className="font-medium">Quantity:</span> {procurement.total_quantity || 0} kg</div>
              <div><span className="font-medium">Due Date:</span> {
                procurement.finance?.payment_due_date 
                  ? format(new Date(procurement.finance.payment_due_date), 'PP')
                  : 'N/A'
              }</div>
            </div>

            {/* Item-level breakdown for multi-product procurements */}
            {items.length > 1 && (
              <Collapsible open={itemsExpanded} onOpenChange={setItemsExpanded} className="mt-3">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between p-2 h-auto">
                    <span className="text-xs">View item breakdown</span>
                    {itemsExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <div className="bg-background rounded-md p-2 space-y-1">
                    {items.map((item: any) => {
                      const itemQuality = item.item_quality?.[0];
                      return (
                        <div key={item.id} className="flex justify-between items-center text-xs border-b border-border/50 pb-1 last:border-0 last:pb-0">
                          <div>
                            <span className="font-medium">{item.product_name}</span>
                            <span className="text-muted-foreground ml-1">({item.quantity} kg)</span>
                          </div>
                          <div className="flex gap-2">
                            {itemQuality?.quality_grade && (
                              <Badge variant="outline" className="text-xs h-5">
                                {itemQuality.quality_grade}
                              </Badge>
                            )}
                            {(itemQuality?.compensation_kg > 0 || itemQuality?.preliminary_compensation_kg > 0) && (
                              <span className="text-amber-600">
                                -{itemQuality?.compensation_kg || itemQuality?.preliminary_compensation_kg || 0}kg
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>

          {/* Alternative Bank Account Information */}
          {procurement.finance?.use_different_bank && procurement.finance?.bank_name && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
              <h4 className="font-medium mb-2 text-blue-800">🏦 Payment Account (Alternative)</h4>
              <div className="space-y-1 text-sm text-blue-700">
                <div><span className="font-medium">Bank:</span> {procurement.finance.bank_name}</div>
                {procurement.finance.bank_account && (
                  <div><span className="font-medium">Account:</span> {procurement.finance.bank_account}</div>
                )}
                {procurement.finance.bank_branch && (
                  <div><span className="font-medium">Branch:</span> {procurement.finance.bank_branch}</div>
                )}
                {procurement.finance.account_holder_name && (
                  <div><span className="font-medium">Account Holder:</span> {procurement.finance.account_holder_name}</div>
                )}
              </div>
            </div>
          )}

          {/* Quality Inspection - Preliminary vs Final Comparison */}
          {quality && (quality.damage_kg > 0 || quality.compensation_kg > 0 || isPreliminary) && (
            <div className="space-y-3">
              {/* Preliminary Assessment */}
              {isPreliminary && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
                  <h4 className="font-medium mb-2 text-amber-800">
                    📋 Preliminary Assessment (Sample-based)
                  </h4>
                  <div className="space-y-1 text-sm text-amber-700">
                    <div><span className="font-medium">Estimated Damage:</span> {quality.preliminary_damage_kg || 0} kg</div>
                    <div><span className="font-medium">Estimated Compensation:</span> {preliminaryCompensation} kg</div>
                    <div className="pt-1 border-t border-amber-200">
                      <span className="font-medium">Net Payable (Preliminary):</span> {(procurement.total_quantity || 0) - preliminaryCompensation} kg
                    </div>
                    {quality.final_grading_expected_date && !isFinalComplete && (
                      <div className="pt-1 text-amber-600">
                        <span className="font-medium">Expected Final Grading:</span> {format(new Date(quality.final_grading_expected_date), 'PP')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Final Assessment */}
              {isFinalComplete && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-md">
                  <h4 className="font-medium mb-2 text-green-800">
                    ✅ Final Assessment (Complete)
                  </h4>
                  <div className="space-y-1 text-sm text-green-700">
                    <div><span className="font-medium">Actual Damage:</span> {quality.damage_kg || 0} kg</div>
                    <div><span className="font-medium">Actual Compensation:</span> {finalCompensation} kg</div>
                    <div className="pt-1 border-t border-green-200">
                      <span className="font-medium">Net Payable (Final):</span> {(procurement.total_quantity || 0) - finalCompensation} kg
                    </div>
                  </div>
                </div>
              )}

              {/* Difference/Reconciliation */}
              {hasFinalGrading && compensationDifference !== 0 && (
                <div className={`p-4 rounded-md ${compensationDifference > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <h4 className={`font-medium mb-2 flex items-center gap-2 ${compensationDifference > 0 ? 'text-red-800' : 'text-green-800'}`}>
                    <AlertTriangle className="h-4 w-4" />
                    Reconciliation Required
                  </h4>
                  <div className={`text-sm ${compensationDifference > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    <div><span className="font-medium">Difference:</span> {Math.abs(compensationDifference)} kg</div>
                    <div className="mt-1">
                      {compensationDifference > 0 
                        ? `⚠️ Final damage was ${Math.abs(compensationDifference)} kg MORE than estimated. Supplier may be owed less.`
                        : `✅ Final damage was ${Math.abs(compensationDifference)} kg LESS than estimated. Additional payment may be due.`
                      }
                    </div>
                  </div>
                </div>
              )}

              {/* Non-preliminary quality findings */}
              {!isPreliminary && (quality.damage_kg > 0 || quality.compensation_kg > 0) && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
                  <h4 className="font-medium mb-2 text-amber-800">Quality Inspection Findings</h4>
                  <div className="space-y-1 text-sm text-amber-700">
                    <div><span className="font-medium">Damage Found:</span> {quality.damage_kg || 0} kg</div>
                    <div><span className="font-medium">Agreed Compensation:</span> {quality.compensation_kg || 0} kg</div>
                    <div className="pt-1 border-t border-amber-200">
                      <span className="font-medium">Net Payable:</span> {(procurement.total_quantity || 0) - (quality.compensation_kg || 0)} kg
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VAT Mode Selection */}
          <div className="space-y-3 border rounded-md p-4">
            <Label className="text-base font-medium">VAT Handling</Label>
            <RadioGroup value={vatMode} onValueChange={(v) => setVatMode(v as VatMode)} className="space-y-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="vat-none" />
                <Label htmlFor="vat-none" className="font-normal cursor-pointer">No VAT</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="add" id="vat-add" />
                <Label htmlFor="vat-add" className="font-normal cursor-pointer">Add VAT ({defaultVatRate ?? 15}%)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deduct" id="vat-deduct" />
                <Label htmlFor="vat-deduct" className="font-normal cursor-pointer">Deduct VAT (amount is VAT-inclusive)</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Withholding Tax */}
          <div className="space-y-3 border rounded-md p-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Withholding Tax</Label>
              <Switch checked={withholdingApplicable} onCheckedChange={setWithholdingApplicable} />
            </div>
            {withholdingApplicable && (
              <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select withholding rate" />
                </SelectTrigger>
                <SelectContent>
                  {withholdingRates?.map((rate) => (
                    <SelectItem key={rate.id} value={rate.id}>
                      {rate.name} ({rate.rate}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Tax Summary */}
          <div className="bg-muted/50 border rounded-md p-4 space-y-2 text-sm">
            <h4 className="font-medium">Payment Summary</h4>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span>Base Amount:</span>
                <span>Br{paymentTotals.baseAmount.toFixed(2)}</span>
              </div>
              {paymentTotals.vatRate > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>VAT ({paymentTotals.vatRate}%):</span>
                  <span>Br{paymentTotals.vatAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1">
                <span>Subtotal:</span>
                <span>Br{paymentTotals.subtotal.toFixed(2)}</span>
              </div>
              {paymentTotals.withholdingRate > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Withholding ({paymentTotals.withholdingRate}%):</span>
                  <span>-Br{paymentTotals.withholdingAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t pt-1">
                <span>Net Payment:</span>
                <span className="text-green-600">Br{paymentTotals.netPayment.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paidFromAccount">Paid From Account *</Label>
            <Select value={paidFromAccountId} onValueChange={setPaidFromAccountId}>
              <SelectTrigger id="paidFromAccount">
                <SelectValue placeholder={bankAccountsLoading ? "Loading accounts..." : "Select bank account"} />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="actualPaymentDate">Actual Payment Date</Label>
            <Input
              id="actualPaymentDate"
              type="date"
              value={actualPaymentDate}
              onChange={(e) => setActualPaymentDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmationNotes">Confirmation Notes (Optional)</Label>
            <Textarea
              id="confirmationNotes"
              placeholder="Add any notes about the payment confirmation..."
              value={confirmationNotes}
              onChange={(e) => setConfirmationNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmPayment} disabled={isSubmitting || !paidFromAccountId}>
            {isSubmitting ? 'Confirming...' : 'Confirm Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
